/* eslint-disable @typescript-eslint/no-this-alias */
/* eslint-disable no-underscore-dangle */
import AwcAutocompleteElement from '@ambiki/impulse-view-components/dist/elements/autocomplete';
import ApplicationController from './application_controller';
import { closeOnEsc } from '../lib/helpers';

export default class extends ApplicationController {
  static targets = ['form', 'searchOptions', 'filterTxt', 'filter', 'filters', 'icon', 'reset', 'update'];

  initialize() {
    this.open = false;
    this.filters = [];
  }

  connect() {
    closeOnEsc(this.closeFilters);
    // Set up change events on filters
    this.listenForFilterChanges();
    this.checkForExistingAutocomplete();
    this.checkForExistingCheckboxes();
    this.checkForExistingNumbers();
    this.checkForExistingDates();
    this.checkForExistingDateTimes();
  }

  /**
   * Remove all filters before Turbo caches the page. Since, we append the filters in the `connect` function, we
   * can safely remove them and it will automatically be appended when the controller is connected to the DOM.
   */
  disconnect() {
    if (!this.hasFiltersTarget) return;

    const filters = Array.from(this.filtersTarget.querySelectorAll('[data-behavior="filter-wrapper"]'));
    filters.forEach((filter) => filter.remove());
  }

  closeFilters = () => {
    this.searchOptionsTarget.classList.add('d-none');
    this.iconTarget.classList.remove('bi-chevron-up');
    this.open = false;
  };

  openFilters = () => {
    this.searchOptionsTarget.classList.remove('d-none');
    this.iconTarget.classList.add('bi-chevron-up');
    this.open = true;
  };

  toggleFilters() {
    return this.searchOptionsTarget.classList.contains('d-none') ? this.openFilters() : this.closeFilters();
  }

  toggleFilterList() {
    if (this.filters.length) {
      this.filtersTarget.classList.remove('d-none');
    } else this.filtersTarget.classList.add('d-none');
  }

  toggleRefreshBtn() {
    if (this.filters.length) {
      this.updateTarget.classList.remove('d-none');
    } else this.updateTarget.classList.add('d-none');
  }

  toggleResetBtn() {
    if (this.filters.length) {
      this.resetTarget.classList.remove('d-none');
    } else this.resetTarget.classList.add('d-none');
  }

  closeOnOutsideClick(event) {
    if (!this.open) return;
    if (this.element.contains(event.target)) return;
    this.closeFilters();
  }

  checkForExistingAutocomplete() {
    this.filterTargets.forEach((filter) => {
      if (filter instanceof AwcAutocompleteElement) {
        customElements.whenDefined(filter.identifier).then(() => {
          if (this.autocompleteHasSelection(filter)) {
            this.handleFilter(filter);
          }
        });
      }
    });
  }

  /**
   * Whether an autocomplete arrived with something selected, read from the rendered markup rather
   * than from `element.value`. An Impulse element only learns it is a multi-select once it
   * hydrates, which happens after this runs, so until then a multi-select is read down the
   * single-select branch and reports the blank hidden field it finds there, looking empty. Its
   * selected values are server-rendered as tags, so the markup is right from the first parse.
   * Reading `value` after hydration would not do either: it returns [] for an empty multi-select,
   * and an empty array is truthy, so every unset multi-select would get a filter chip.
   */
  autocompleteHasSelection(autocomplete) {
    if (autocomplete.querySelector('[data-behavior="tag"]')) return true;

    return Array.from(autocomplete.querySelectorAll('input[data-behavior="hidden-field"]')).some(
      (field) => field.value !== ''
    );
  }

  checkForExistingDates() {
    this.filterTargets.forEach((filter) => {
      if (filter.type === 'date' && filter.value) {
        this.handleFilter(filter);
      }
    });
  }

  checkForExistingDateTimes() {
    this.filterTargets.forEach((filter) => {
      if (filter.type === 'datetime-local' && filter.value) {
        this.handleFilter(filter);
      }
    });
  }

  checkForExistingCheckboxes() {
    this.filterTargets.forEach((filter) => {
      if (filter.type === 'checkbox' && filter.checked) this.handleFilter(filter);
    });
  }

  checkForExistingNumbers() {
    this.filterTargets.forEach((filter) => {
      if (filter.type === 'number' && filter.value !== undefined && filter.value !== '') this.handleFilter(filter);
    });
  }

  handleFilter(filter) {
    const id =
      filter instanceof AwcAutocompleteElement
        ? filter.querySelector('input[type="text"]')?.id
        : filter.getAttribute('id');
    if (this.filters.find((f) => f.id === id)) {
      return;
    }

    const { name } = filter.dataset;
    const filterObj = { id, name };
    this.filters.push(filterObj);

    const li = document.createElement('li');
    const div = document.createElement('div');
    const icon = document.createElement('i');
    icon.classList = 'bi bi-x';
    li.classList = 'list-inline-item';
    li.setAttribute('data-behavior', 'filter-wrapper');
    div.classList = 'search-options tag';
    div.textContent = filterObj.name;
    div.append(icon);
    li.append(div);
    li.addEventListener('click', (e) => this.removeFilter(e.target.parentElement));
    this.filtersTarget.prepend(li);
    this.updateBtnText();
    this.toggleResetBtn();
    this.toggleFilterList();
  }

  removeFilter(el) {
    const elFilter = this.filters.find((filter) => filter.name === el.textContent);
    const filtered = this.filters.filter((filter) => filter.name !== el.textContent);
    this.filters = filtered;
    const selectField = $(`#${elFilter.id}`)[0];

    const autocomplete = selectField.closest('awc-autocomplete');
    if (autocomplete) {
      autocomplete.clear();
    } else if (selectField.selectize) {
      selectField.selectize.clear();
    } else if (selectField.type === 'checkbox') {
      selectField.checked = false;
    } else if (selectField.type === 'number' || selectField.type === 'date') {
      selectField.value = '';
    }
    (el.closest('[data-behavior="filter-wrapper"]') || el).remove();
    this.updateBtnText();
    this.toggleRefreshBtn();
    this.toggleResetBtn();
    this.toggleFilterList();
  }

  updateBtnText() {
    const label = this.filters.length
      ? `${this.filters.length} filter${this.filters.length === 1 ? '' : 's'}`
      : 'Filters';
    this.filterTxtTarget.textContent = label;
  }

  listenForFilterChanges() {
    const selectizeOptions = {
      'filterable-no-sort': { create: false },
      'filterable-text': { create: false, sortField: 'text' },
      filterable: { create: false, sortField: 'id' },
    };
    this.filterTargets.forEach((filter) => {
      const _this = this;
      const { selectize } = filter.dataset;
      if (selectize) {
        const options = {
          ...selectizeOptions[selectize],
          ...{
            onChange(value) {
              const filterObj = _this.filters.find((f) => f.id === filter.getAttribute('id'));
              if (value && !filterObj) _this.handleFilter(filter);
            },
            onInitialize() {
              if (this.items.length) _this.handleFilter(filter);
            },
          },
        };
        $(filter).selectize(options);
      } else if (filter instanceof AwcAutocompleteElement) {
        filter.addEventListener('awc-autocomplete:commit', () => this.handleFilter(filter));
      } else if (filter.type === 'date') {
        filter.addEventListener('change', (e) => {
          if (filter.value) {
            this.handleFilter(filter);
          } else {
            const filterObj = this.filters.find((f) => f.id === filter.getAttribute('id'));
            if (filterObj) {
              const tag = Array.from(document.querySelectorAll('.search-options.tag')).find(
                (t) => t.textContent === filterObj.name
              );
              if (tag) this.removeFilter(tag);
            }
          }
        });
      } else {
        filter.addEventListener('change', (e) => {
          if (
            (e.target.checked && filter.type === 'checkbox') ||
            (filter.type !== 'checkbox' && filter.value !== undefined && filter.value !== '')
          ) {
            this.handleFilter(filter);
          } else {
            // find the state filter
            const filterObj = this.filters.find((f) => f.id === filter.getAttribute('id'));
            if (filterObj) {
              const tag = Array.from(document.querySelectorAll('.search-options.tag')).find(
                (t) => t.textContent === filterObj.name
              );
              if (tag) this.removeFilter(tag);
            }
          }
        });
      }
    });
  }

  reset() {
    // eslint-disable-next-line prefer-destructuring
    window.location.href = window.location.href.split('?')[0];
  }

  reload() {
    this.formTarget.submit();
  }
}
